import type { ReactNode } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "../components/layout/Layout";

const h2 = (text: string) => (
  <h2
    style={{
      fontFamily: "var(--font-display)",
      fontSize: "22px",
      fontWeight: "700",
      color: "var(--text-primary)",
      marginTop: "48px",
      marginBottom: "16px",
    }}
  >
    {text}
  </h2>
);

const POSTS: Record<
  string,
  {
    category: string;
    categoryColor: string;
    tagBg: string;
    date: string;
    readTime: string;
    title: string;
    subtitle: string;
    body: ReactNode;
  }
> = {
  "stockouts-craft-beverage": {
    category: "Inventory",
    categoryColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.2)",
    date: "Mar 12, 2026",
    readTime: "6 min read",
    title: "The real cost of stockouts in craft beverage distribution",
    subtitle:
      "A single stockout cascades into lost retailer trust, missed seasonals, and months of recovery. We break down the hidden math.",
    body: (
      <>
        <p>
          A stockout is never just a stockout. The moment a retailer discovers
          you can&apos;t fulfill their order — especially during peak season —
          the damage starts compounding before a single apology is sent.
        </p>
        <p>
          For craft beverage distributors, the stakes are higher than in most
          categories. Your products are seasonal, highly perishable, and built
          on relationships. Lose a slot during a key weekend, and you may not
          get it back until next year.
        </p>
        {h2("The direct cost is only a fraction of the real number")}
        <p>
          Most distributors calculate stockout cost as the value of the missed
          order. That&apos;s the smallest part. The real cost includes emergency
          re-routing expenses, sales rep time spent on damage control, and
          markdowns on ageing inventory elsewhere. For a mid-size distributor, a
          single stockout event can cost ₹4–8 lakh when all factors are
          included.
        </p>
        {h2("Why craft beverage is especially vulnerable")}
        <p>
          Large-format FMCG can absorb some stockouts because SKU substitution
          is easier. Craft beverage doesn&apos;t work that way — a retailer who
          ordered a specific seasonal IPA cannot substitute it with a different
          product and call it done. The relationship between a craft distributor
          and a premium outlet is built on reliability.
        </p>
        {h2("How to reduce stockouts systematically")}
        <p>
          The fix isn&apos;t more safety stock — it&apos;s better signal. Most
          stockouts are caused by demand data arriving too late.
        </p>
        <ul style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Move orders online:</strong> Digital orders give you demand
            signal hours or days earlier — enough time to flag shortfalls before
            they become missed deliveries.
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Set reorder triggers per SKU:</strong> Each product should
            have a defined minimum quantity that triggers replenishment — not a
            weekly review meeting.
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Track by warehouse location:</strong> Aggregate numbers hide
            the real problem. You may have 200 cases total, but only 12 are
            where tomorrow&apos;s deliveries run from.
          </li>
        </ul>
        {h2("The bottom line")}
        <p>
          Stockouts are a data problem masquerading as a supply problem. Fix the
          data flow, and the stockouts drop sharply.
        </p>
      </>
    ),
  },
  "managing-credit": {
    category: "Finance",
    categoryColor: "#b84d00",
    tagBg: "rgba(232,106,13,0.2)",
    date: "Mar 5, 2026",
    readTime: "5 min read",
    title:
      "How to set credit limits that protect cash flow without losing customers",
    subtitle:
      "Setting the right credit limits for retail partners is part science, part gut feeling. Here's how to do it systematically.",
    body: (
      <>
        <div
          style={{
            background: "rgba(31,95,91,0.06)",
            border: "1px solid rgba(31,95,91,0.15)",
            borderRadius: "10px",
            padding: "20px 24px",
            marginBottom: "40px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "var(--text-primary)",
              lineHeight: "1.7",
            }}
          >
            <strong>TL;DR:</strong> Credit limits exist to protect your cash
            flow, but setting them too conservatively drives buyers to
            competitors. The right limit is based on purchase history, payment
            behaviour, and your own working capital — not gut instinct alone.
          </p>
        </div>
        <p>
          Every brewery distributor has been burned by a slow-paying buyer at
          some point. A retailer builds up ₹4 lakh in outstanding dues, goes
          quiet for six weeks, and suddenly you&apos;re chasing invoices instead
          of growing your network. The natural reaction is to tighten credit
          across the board — but blanket restrictions punish your best buyers
          too.
        </p>
        {h2("Why most credit limits are set wrong")}
        <p>
          Most distributors set credit limits in one of two ways: they copy what
          they&apos;ve always done (a round number based on instinct), or they
          set a limit once during onboarding and never revisit it. Both
          approaches break down quickly.
        </p>
        {h2("The three-variable framework")}
        <ol style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Average monthly order value (trailing 3 months):</strong> A
            buyer ordering ₹1.2 lakh per month warrants a limit of 1.5–2× that
            figure.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Days Sales Outstanding (DSO):</strong> A 30-day DSO buyer is
            far less risky than a 75-day DSO buyer, even with identical order
            volumes.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Your own working capital position:</strong> Credit limits
            are also a function of how much you can afford to carry at any given
            time.
          </li>
        </ol>
        {h2("Making limits visible to buyers")}
        <p>
          When a buyer doesn&apos;t know they&apos;re approaching their limit,
          trust erodes. Showing buyers their outstanding balance and available
          credit before they place an order turns the conversation proactive.
        </p>
        {h2("The bottom line")}
        <p>
          Set limits based on data. Review them regularly. Make them visible.
          Enforce them consistently. That&apos;s the foundation of a receivables
          process that scales.
        </p>
      </>
    ),
  },
  "route-density": {
    category: "Distribution",
    categoryColor: "#334155",
    tagBg: "rgba(51,65,85,0.15)",
    date: "Feb 26, 2026",
    readTime: "7 min read",
    title:
      "Route density vs. frequency: how top distributors think about van planning",
    subtitle:
      "More stops or more visits? We analysed data from 40 distributors to find which approach actually wins.",
    body: (
      <>
        <p>
          Every distribution manager has wrestled with the same tradeoff: should
          a van cover more outlets per run (density), or revisit the same
          outlets more often (frequency)? The answer changes your entire fleet
          model, staffing levels, and revenue ceiling.
        </p>
        {h2("What the data shows")}
        <p>
          Distributors who prioritised route density consistently showed higher
          revenue per van per month in urban markets. But in tier-2 cities and
          semi-urban markets, the picture flipped — distributors who visited
          fewer outlets more frequently outperformed on retention and average
          order value.
        </p>
        {h2("The cost of over-density")}
        <p>
          Packing too many stops into a route sounds efficient but creates
          hidden costs. Drivers rush, relationship quality drops, and lapsed
          outlets never get the attention needed to re-engage them. Over time,
          route density without frequency discipline quietly erodes your active
          outlet count.
        </p>
        {h2("A practical framework")}
        <ul style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Classify outlets by order frequency:</strong> Weekly,
            bi-weekly, monthly. Route them accordingly.
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Track lapsed outlets separately:</strong> Any outlet not
            ordering in 21+ days should get a re-engagement visit.
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            <strong>Measure revenue per kilometre driven:</strong> This single
            metric surfaces over-extended routes faster than any other KPI.
          </li>
        </ul>
        {h2("The bottom line")}
        <p>
          Density wins in dense urban markets. Frequency wins where relationship
          depth drives orders. Most distributors need a hybrid model — and the
          only way to tune it is to measure outlet-level performance rather than
          route-level averages.
        </p>
      </>
    ),
  },
  "order-engine": {
    category: "Operations",
    categoryColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.2)",
    date: "Feb 18, 2026",
    readTime: "4 min read",
    title:
      "What 500 orders a day taught us about building Maltora's order engine",
    subtitle:
      "From the product team: the decisions that shaped how Maltora processes, routes, and confirms orders in real time.",
    body: (
      <>
        <p>
          When we started building Maltora&apos;s order engine, we assumed the
          hard problem was handling volume. Turns out the harder problem is
          handling ambiguity — orders with missing SKUs, partial quantities,
          outdated pricing, or buyers who haven&apos;t cleared previous dues.
        </p>
        {h2('The problem with "simple" order flows')}
        <p>
          Early versions were clean: buyer places order, seller confirms,
          warehouse picks, delivery goes out. In practice, roughly 30% of orders
          need some form of intervention before they can be confirmed — a credit
          check, a stock substitution, a price correction, or a delivery date
          negotiation.
        </p>
        {h2("Three decisions that shaped the architecture")}
        <ol style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Orders are confirmed, not just received.</strong> Every
            order goes through an explicit confirmation step with live context
            before entering fulfilment.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>
              Credit checks happen at order time, not invoicing time.
            </strong>{" "}
            Catching a credit breach earlier means the conversation is easier
            and the outcome is better.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Every state change is logged with a reason.</strong> This
            audit trail becomes invaluable when disputes arise weeks later.
          </li>
        </ol>
        {h2("What we'd do differently")}
        <p>
          We underestimated how much of order management is communication, not
          just data processing. If we were starting over, we&apos;d build the
          communication layer first and the data layer second.
        </p>
      </>
    ),
  },
  "batch-tracking": {
    category: "Inventory",
    categoryColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.2)",
    date: "Feb 10, 2026",
    readTime: "9 min read",
    title:
      "Batch tracking for craft beverage: compliance, freshness, and recalls done right",
    subtitle:
      "Batch tracking isn't optional — it's your safety net. Here's how to implement it without slowing down operations.",
    body: (
      <>
        <div
          style={{
            background: "rgba(31,95,91,0.06)",
            border: "1px solid rgba(31,95,91,0.15)",
            borderRadius: "10px",
            padding: "20px 24px",
            marginBottom: "40px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "var(--text-primary)",
              lineHeight: "1.7",
            }}
          >
            <strong>TL;DR:</strong> Batch tracking lets you trace every unit
            back to its production run — critical for compliance audits,
            freshness management, and product recalls.
          </p>
        </div>
        <p>
          A batch recall without proper tracking is a distributor&apos;s worst
          nightmare. Without knowing exactly which outlets received product from
          a specific production run, you&apos;re either recalling everything
          (expensive) or nothing (a regulatory risk).
        </p>
        {h2("What batch tracking actually covers")}
        <p>
          Good batch tracking means you can answer three questions at any time:
          which outlets hold product from batch X, what is the remaining shelf
          life of warehouse inventory, and if a recall is needed — which
          deliveries were affected and when?
        </p>
        {h2("Why most implementations fail")}
        <p>
          The most common failure mode is inconsistent capture — batch numbers
          are recorded at inbound but not tracked through picking or delivery.
          By the time product reaches the outlet, the batch link is broken.
        </p>
        {h2("A practical implementation approach")}
        <ol style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Record at inbound:</strong> Every goods receipt captures
            batch number, production date, and best-before date. No exceptions.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>FEFO picking:</strong> First-Expired-First-Out enforced at
            pick time, not managed by warehouse staff memory.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Delivery manifests include batch:</strong> Every delivery
            note specifies the batch numbers included — your downstream
            traceability record.
          </li>
          <li style={{ marginBottom: "16px", lineHeight: "1.8" }}>
            <strong>Set expiry alerts:</strong> Any batch within 30 days of
            best-before triggers an automatic alert to operations.
          </li>
        </ol>
        {h2("The bottom line")}
        <p>
          Batch tracking done properly is an operational advantage, not just a
          compliance burden. It lets you respond to incidents in hours rather
          than days.
        </p>
      </>
    ),
  },
  "changelog-jan": {
    category: "Product Update",
    categoryColor: "#b84d00",
    tagBg: "rgba(232,106,13,0.2)",
    date: "Jan 30, 2026",
    readTime: "3 min read",
    title:
      "Maltora changelog: predictive restocking, multi-currency, and the new sales rep app",
    subtitle:
      "Everything we shipped in January — the features our Growth customers asked for most this quarter.",
    body: (
      <>
        <p>
          January was one of our biggest shipping months. Here&apos;s everything
          that went live for Growth and Enterprise customers.
        </p>
        {h2("Predictive restocking alerts")}
        <p>
          Maltora now analyses order velocity per SKU and warehouse location,
          surfacing restocking alerts before you hit your minimum threshold —
          not after. You can configure lead time per supplier, and the alert
          factors that in when flagging urgency.
        </p>
        {h2("Multi-currency support")}
        <p>
          Enterprise customers with cross-border distribution can now set buyer
          accounts to transact in local currency. Invoices, credit limits, and
          payment records all reflect the account currency, with conversion
          rates locked at order time. Currently supported: INR, USD, AED, SGD,
          GBP.
        </p>
        {h2("New sales rep mobile app")}
        <p>The redesigned app is now live on iOS and Android:</p>
        <ul style={{ paddingLeft: "24px", marginBottom: "24px" }}>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            Outlet visit logging with GPS timestamp
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            Outstanding dues visible per outlet before the conversation starts
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            Order placement with live inventory confirmation
          </li>
          <li style={{ marginBottom: "12px", lineHeight: "1.8" }}>
            Offline mode for poor connectivity areas
          </li>
        </ul>
        {h2("Coming in Q2")}
        <p>
          Payment collection workflows, buyer self-service payment portal, and
          deeper analytics for finance teams. Reach out to your account manager
          or email support@maltora.app with specific requests.
        </p>
      </>
    ),
  },
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? POSTS[slug] : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <Layout>
      <section
        style={{
          padding: "100px 0 56px",
          background: "linear-gradient(135deg,var(--teal) 0%,#0F2D2B 100%)",
          position: "relative",
          overflowX: "hidden",
        }}
        aria-labelledby="post-title"
      >
        <div className="container section-reveal">
          <div style={{ maxWidth: "720px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "24px",
              }}
            >
              <Link
                to="/blog"
                style={{
                  fontSize: "13px",
                  color: "rgba(247,244,239,0.6)",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "rgba(247,244,239,0.9)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(247,244,239,0.6)")
                }
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Blog
              </Link>
              <span style={{ color: "rgba(247,244,239,0.3)" }}>·</span>
              <span
                style={{
                  display: "inline-flex",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  background: post.tagBg,
                  color: "var(--orange)",
                }}
              >
                {post.category}
              </span>
            </div>
            <h1
              id="post-title"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(28px,4vw,44px)",
                fontWeight: "700",
                color: "var(--text-on-dark)",
                lineHeight: "1.15",
                letterSpacing: "-0.02em",
                marginBottom: "20px",
              }}
            >
              {post.title}
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "rgba(247,244,239,0.75)",
                lineHeight: "1.65",
                maxWidth: "580px",
                marginBottom: "32px",
              }}
            >
              {post.subtitle}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#1F5F5B,#2d8078)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#fff",
                }}
              >
                M
              </div>
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "rgba(247,244,239,0.9)",
                  }}
                >
                  Maltora Team
                </div>
                <div
                  style={{ fontSize: "12px", color: "rgba(247,244,239,0.5)" }}
                >
                  {post.date} · {post.readTime}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        style={{
          maxWidth: "740px",
          margin: "0 auto",
          padding: "64px 24px 96px",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            color: "var(--text-muted)",
            lineHeight: "1.9",
          }}
        >
          {post.body}
        </div>

        <div
          style={{
            marginTop: "64px",
            padding: "36px",
            background: "var(--bg-cream,#F7F4EF)",
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "22px",
              fontWeight: "700",
              marginBottom: "12px",
            }}
          >
            Want to see Maltora in action?
          </h3>
          <p
            style={{
              color: "var(--text-muted)",
              marginBottom: "24px",
              lineHeight: "1.65",
            }}
          >
            See how Maltora helps beverage distributors run tighter, faster
            operations.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/contact"
              className="btn btn-primary"
              style={{ padding: "12px 28px" }}
            >
              Get in touch
            </Link>
            <Link
              to="/blog"
              style={{
                padding: "12px 28px",
                display: "inline-flex",
                alignItems: "center",
                border: "1.5px solid rgba(15,23,42,0.2)",
                borderRadius: "10px",
                color: "var(--text-primary)",
                background: "transparent",
                fontFamily: "var(--font-ui)",
                fontSize: "15px",
                fontWeight: "600",
                textDecoration: "none",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--teal)";
                e.currentTarget.style.color = "var(--teal)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(15,23,42,0.2)";
                e.currentTarget.style.color = "var(--text-primary)";
              }}
            >
              More articles
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
